/**
 * When to write the playhead back to the server.
 *
 * Constraints this encodes, all learned the hard way:
 *  - it must never fire before the resume seek has landed, or a `timeupdate` at
 *    0:00 overwrites the position we were about to restore;
 *  - it must never fire while a presigned-URL refresh is in flight, because
 *    `currentTime` is transiently 0 across the source swap;
 *  - a held arrow key repeats ~30x/second, and each repeat lands a `seeked`.
 *    Sending on `seeked` directly would blow a 60/min budget in two seconds and
 *    then silently stop saving for a minute, so seeks only ARM a trailing timer
 *    and the host fires one `seekQuiet` when the learner stops.
 */
export const HEARTBEAT_INTERVAL_MS = 10_000;
/** Quiet period after the last seek before one coalesced write goes out. */
export const SEEK_QUIET_MS = 1_500;
/** How long to stand down after a 429. */
export const THROTTLE_BACKOFF_MS = 60_000;
/** Sub-second drift is not worth a request. */
const MIN_DELTA_SECONDS = 1;

export type HeartbeatState = {
  lastSentAt: number | null;
  lastSentPos: number | null;
  suppressUntil: number | null;
};

export type HeartbeatEventKind =
  | 'tick'
  | 'pause'
  | 'seeked'
  | 'seekQuiet'
  | 'hidden'
  | 'blur'
  | 'pagehide'
  | 'ended'
  | 'lessonSwitch';

export type HeartbeatEvent = {
  kind: HeartbeatEventKind;
  position: number;
  now: number;
  playing: boolean;
  /** No progress row exists for a logged-out preview viewer. */
  guest: boolean;
  /** The resume seek has landed; before that the playhead is not the truth. */
  resumed: boolean;
  refreshing: boolean;
  /**
   * Furthest second WATCHED, from the seek policy. Rides every write so the
   * server's monotonic maximum keeps up without a second request — and so the
   * `ended` write below, which deliberately stores position 0, cannot be read
   * as "this learner has watched nothing".
   */
  maxPosition?: number;
};

export type HeartbeatDecision = {
  send: boolean;
  position: number;
  /** The watched high-water mark to send alongside `position`. */
  maxPosition: number;
  /** True on the write that reports the learner reached the end. */
  ended: boolean;
  /** `keepalive` survives the document being torn down; ordinary writes do not. */
  transport: 'api' | 'keepalive';
  state: HeartbeatState;
};

export function initialHeartbeatState(): HeartbeatState {
  return { lastSentAt: null, lastSentPos: null, suppressUntil: null };
}

const skip = (state: HeartbeatState, position: number): HeartbeatDecision => ({
  send: false,
  position,
  maxPosition: 0,
  ended: false,
  transport: 'api',
  state,
});

export function decideHeartbeat(state: HeartbeatState, event: HeartbeatEvent): HeartbeatDecision {
  const position = Math.max(0, Math.round(event.position));
  // The mark can never be behind the playhead we are about to store.
  const maxPosition = Math.max(0, Math.round(event.maxPosition ?? 0), position);

  // Ended is the one write that must always land: it resets the resume point so
  // a finished lesson reopens at the start.
  if (event.kind === 'ended') {
    if (event.guest) return skip(state, 0);
    return {
      send: true,
      position: 0,
      // Position 0 with the mark and the `ended` flag in the SAME write: this
      // is the write that unlocks free seeking, and splitting it in two would
      // leave a window where the lesson reads as watched-from-the-start.
      maxPosition: Math.max(0, Math.round(event.maxPosition ?? 0), Math.round(event.position)),
      ended: true,
      transport: 'api',
      state: { ...state, lastSentAt: event.now, lastSentPos: 0 },
    };
  }

  if (event.guest || !event.resumed || event.refreshing) return skip(state, position);
  if (state.suppressUntil != null && event.now < state.suppressUntil) return skip(state, position);
  if (event.kind === 'seeked') return skip(state, position);

  const moved =
    state.lastSentPos == null || Math.abs(position - state.lastSentPos) >= MIN_DELTA_SECONDS;

  if (event.kind === 'tick') {
    if (!event.playing) return skip(state, position);
    if (state.lastSentAt != null && event.now - state.lastSentAt < HEARTBEAT_INTERVAL_MS) {
      return skip(state, position);
    }
    if (!moved) return skip(state, position);
  } else if (!moved) {
    return skip(state, position);
  }

  return {
    send: true,
    position,
    maxPosition,
    ended: false,
    transport: event.kind === 'pagehide' ? 'keepalive' : 'api',
    state: { ...state, lastSentAt: event.now, lastSentPos: position },
  };
}

/** Stand down for a minute after the server says we are writing too often. */
export function suppressAfterThrottle(state: HeartbeatState, now: number): HeartbeatState {
  return { ...state, suppressUntil: now + THROTTLE_BACKOFF_MS };
}
